import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import type {
  AffectedContract,
  BulkSendInput,
  BulkSendResult,
  ContractDocument,
  ContractTemplate,
  ContractTemplateSend,
  CreateContractTemplateInput,
  ReplaceContractTemplateInput,
  TemplateDiff,
  UpdateContractTemplateInput,
} from '../types.ts'

// ─── Templates ──────────────────────────────────────────────────────────────

export function useContractTemplates(filter: { productId?: string; active?: boolean } = {}) {
  return useQuery<ContractTemplate[]>({
    queryKey: ['contract-templates', 'list', filter],
    queryFn: () =>
      apiClient
        .get('/contract-templates', {
          params: {
            ...(filter.productId ? { productId: filter.productId } : {}),
            ...(typeof filter.active === 'boolean' ? { active: String(filter.active) } : {}),
          },
        })
        .then((r) => r.data),
  })
}

export function useCreateContractTemplate() {
  const qc = useQueryClient()
  return useMutation<ContractTemplate, Error, CreateContractTemplateInput>({
    mutationFn: (input) => apiClient.post('/contract-templates', input).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contract-templates'] }),
  })
}

export function useUpdateContractTemplate(id: string) {
  const qc = useQueryClient()
  return useMutation<ContractTemplate, Error, UpdateContractTemplateInput>({
    mutationFn: (input) => apiClient.patch(`/contract-templates/${id}`, input).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contract-templates'] }),
  })
}

export function useReplaceContractTemplate(id: string) {
  const qc = useQueryClient()
  return useMutation<{ ok: true; version: number }, Error, ReplaceContractTemplateInput>({
    mutationFn: (input) => apiClient.post(`/contract-templates/${id}/replace`, input).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contract-templates'] }),
  })
}

export function useDeleteContractTemplate() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.delete(`/contract-templates/${id}`).then(() => undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contract-templates'] }),
  })
}

export function templateDownloadUrl(id: string): string {
  return `/api/contract-templates/${id}/download`
}

export function useTemplateVersions(id: string | null) {
  return useQuery<ContractTemplate[]>({
    queryKey: ['contract-templates', 'versions', id],
    queryFn: () => apiClient.get(`/contract-templates/${id}/versions`).then((r) => r.data),
    enabled: Boolean(id),
  })
}

export function useTemplateDiff(id: string | null, vsId: string | null) {
  return useQuery<TemplateDiff>({
    queryKey: ['contract-templates', 'diff', id, vsId],
    queryFn: () =>
      apiClient.get(`/contract-templates/${id}/diff`, { params: { vs: vsId } }).then((r) => r.data),
    enabled: Boolean(id) && Boolean(vsId),
  })
}

export function useAffectedContracts(id: string | null) {
  return useQuery<AffectedContract[]>({
    queryKey: ['contract-templates', 'affected-contracts', id],
    queryFn: () => apiClient.get(`/contract-templates/${id}/affected-contracts`).then((r) => r.data),
    enabled: Boolean(id),
  })
}

export function useTemplateSends(id: string | null) {
  return useQuery<ContractTemplateSend[]>({
    queryKey: ['contract-templates', 'sends', id],
    queryFn: () => apiClient.get(`/contract-templates/${id}/sends`).then((r) => r.data),
    enabled: Boolean(id),
  })
}

export function useBulkSend(id: string) {
  const qc = useQueryClient()
  return useMutation<BulkSendResult, Error, BulkSendInput>({
    mutationFn: (input) =>
      apiClient.post(`/contract-templates/${id}/bulk-send`, input).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contract-templates', 'sends', id] })
    },
  })
}

// ─── Contract Documents ─────────────────────────────────────────────────────

export function useContractDocuments(contractId: string | undefined) {
  return useQuery<ContractDocument[]>({
    queryKey: ['contract-documents', 'list', contractId],
    queryFn: () => apiClient.get(`/contracts/${contractId}/documents`).then((r) => r.data),
    enabled: Boolean(contractId),
  })
}

export function useGenerateContractDocument(contractId: string) {
  const qc = useQueryClient()
  return useMutation<ContractDocument, Error, { templateId: string }>({
    mutationFn: (input) =>
      apiClient.post(`/contracts/${contractId}/documents/generate`, input).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contract-documents', 'list', contractId] })
      void qc.invalidateQueries({ queryKey: ['contracts', 'detail', contractId] })
    },
  })
}

export function useDeleteContractDocument(contractId: string) {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (docId) => apiClient.delete(`/contracts/documents/${docId}`).then(() => undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contract-documents', 'list', contractId] })
    },
  })
}

export interface UpdateDocumentStatusInput {
  status?: 'draft' | 'finalized' | 'sent_for_signature' | 'signed' | 'declined' | 'expired' | 'error'
  notes?: string | null
  signatureProvider?: 'skribble' | 'yousign' | 'none'
  signatureExternalId?: string | null
  signatureStatus?: string | null
}

export function useUpdateContractDocument(contractId: string) {
  const qc = useQueryClient()
  return useMutation<ContractDocument, Error, { docId: string; input: UpdateDocumentStatusInput }>({
    mutationFn: ({ docId, input }) =>
      apiClient.patch(`/contracts/documents/${docId}`, input).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contract-documents', 'list', contractId] })
    },
  })
}

export interface UploadSignedDocumentInput {
  fileContentBase64: string
  fileName?: string
  mimeType?: string
  notes?: string | null
  signatureProvider?: 'skribble' | 'yousign' | 'none'
  signatureExternalId?: string | null
}

export function useUploadSignedDocument(contractId: string) {
  const qc = useQueryClient()
  return useMutation<ContractDocument, Error, { docId: string; input: UploadSignedDocumentInput }>({
    mutationFn: ({ docId, input }) =>
      apiClient.post(`/contracts/documents/${docId}/upload-signed`, input).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contract-documents', 'list', contractId] })
    },
  })
}

export function documentDownloadUrl(docId: string): string {
  return `/api/contracts/documents/${docId}/download`
}

/** Liest eine File-Auswahl als Base64. */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const idx = result.indexOf(',')
      resolve(idx >= 0 ? result.slice(idx + 1) : result)
    }
    reader.onerror = () => reject(reader.error ?? new Error('FileReader-Fehler'))
    reader.readAsDataURL(file)
  })
}
